import { JwtVerifiedCredentialFormatEnum } from '@dynamic-labs/sdk-api-core';

import { getDefaultClient } from '../../../../client/defaultClient';
import { InvalidParamError } from '../../../../errors/InvalidParamError';
import { assertDefined } from '../../../../utils/assertDefined';
import type {
  CoinbaseOnrampOrderPaymentMethod,
  FieldMissingVerificationForCoinbaseOnramp,
} from '../coinbase.types';

type GetMissingVerificationForCoinbaseOnrampOrderResponse =
  FieldMissingVerificationForCoinbaseOnramp[];

type GetMissingVerificationForCoinbaseOnrampOrderParams = {
  paymentMethod: CoinbaseOnrampOrderPaymentMethod;
};

/**
 * This function is used to get a list of fields that are missing verification for a Coinbase onramp order
 *
 * - If the user is missing information for an email or phone number, the field will be returned with the error code MISSING_INFORMATION
 * - If the user is missing verification for an email or phone number, the field will be returned with the error code MISSING_VERIFICATION
 *   and the existing unverified email or phone number will be included in the data field.
 * - If the user's phone number has not been verified in the last 60 days, the field will be returned with the error code VERIFICATION_EXPIRED
 *   and the existing phone number will be included in the data field.
 *
 * @param params.paymentMethod - The payment method that will be used to create the onramp order
 * @param [client] - The Dynamic client instance. Only required when using multiple Dynamic clients.
 * @returns FieldMissingVerificationForCoinbaseOnramp[] - An array of fields that are missing verification for a Coinbase onramp order
 * @throws {InvalidParamError} - If the payment method is not valid
 * @throws {ValueMustBeDefinedError} - If the user is not authenticated
 */
export const getMissingVerificationForCoinbaseOnrampOrder = (
  { paymentMethod }: GetMissingVerificationForCoinbaseOnrampOrderParams,
  client = getDefaultClient()
): GetMissingVerificationForCoinbaseOnrampOrderResponse => {
  // adding the payment method as a param for now just so a user has to pass an object as the first param
  // that way, when we add more payment methods, we can easily add them to the param object
  // and not have to change the function signature
  if (paymentMethod !== 'GUEST_CHECKOUT_APPLE_PAY') {
    throw new InvalidParamError(`Invalid payment method: ${paymentMethod}`);
  }

  const { user } = client;

  assertDefined(user, 'User is not authenticated');

  const fieldsMissingVerification: FieldMissingVerificationForCoinbaseOnramp[] =
    [];

  const emailVerifiedCredential = user.verifiedCredentials.find(
    (credential) => credential.format === JwtVerifiedCredentialFormatEnum.Email
  );

  if (!user.email) {
    // when user has no email at all (verified or not)
    fieldsMissingVerification.push({
      errorCode: 'MISSING_INFORMATION',
      field: 'email',
    });
  } else if (!emailVerifiedCredential) {
    // when user has an email but it is not verified
    fieldsMissingVerification.push({
      data: user.email,
      errorCode: 'MISSING_VERIFICATION',
      field: 'email',
    });
  }

  const phoneVerifiedCredential = user.verifiedCredentials.find(
    (credential) =>
      credential.format === JwtVerifiedCredentialFormatEnum.PhoneNumber
  );

  const phoneNumberVerifiedInPast60Days =
    phoneVerifiedCredential?.verifiedAt &&
    phoneVerifiedCredential.verifiedAt >=
      new Date(Date.now() - 60 * 24 * 60 * 60 * 1000);

  if (!user.phoneNumber) {
    // when user has no phone number at all (verified or not)
    fieldsMissingVerification.push({
      errorCode: 'MISSING_INFORMATION',
      field: 'phoneNumber',
    });
  } else if (!phoneVerifiedCredential) {
    // when user has a phone number but it is not verified
    fieldsMissingVerification.push({
      data: user.phoneNumber,
      errorCode: 'MISSING_VERIFICATION',
      field: 'phoneNumber',
    });
  } else if (!phoneNumberVerifiedInPast60Days) {
    // when user has a verified phone number but it is not verified in the last 60 days
    fieldsMissingVerification.push({
      data: user.phoneNumber,
      errorCode: 'VERIFICATION_EXPIRED',
      field: 'phoneNumber',
    });
  }

  return fieldsMissingVerification;
};
